import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';
import { getTosConfig, useTosConfig } from '../utils/termsConfig';
import { setAgreementsVerified } from '../components/ProtectedRoute';


const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const tosConfig = useTosConfig(); // ToS link target (server-driven version)
  const { currentUser, signup } = useAuth();

  // App config
  const [requireInviteCode, setRequireInviteCode] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Step 1: Invite code validation
  const [inviteCode, setInviteCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeValid, setCodeValid] = useState(false);

  // Fetch app config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${SHOP_API_BASE_URL}/api/v1/config`);
        if (response.ok) {
          const config = await response.json();
          setRequireInviteCode(config.requireInviteCode);
          if (!config.requireInviteCode) {
            setCodeValid(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch app config:', err);
      }
      setConfigLoaded(true);
    };
    fetchConfig();
  }, []);

  // Step 2: Registration
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState('');

  // Email verification (open registration only)
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = inviteCode.trim().toUpperCase();
    if (!trimmed) {
      setCodeError('Please enter an invite code.');
      return;
    }

    setValidating(true);
    setCodeError('');

    try {
      const response = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/invite-codes/${encodeURIComponent(trimmed)}/validate`
      );

      if (!response.ok) {
        setCodeError('Unable to validate code. Please try again.');
        return;
      }

      const data = await response.json();

      if (data.valid) {
        setCodeValid(true);
      } else {
        setCodeError('This invite code is invalid or has already been used.');
      }
    } catch (err) {
      console.error('Failed to validate invite code:', err);
      setCodeError('Unable to validate code. Please check your connection and try again.');
    } finally {
      setValidating(false);
    }
  };

  // Poll for email verification and complete registration
  const verificationPollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!awaitingVerification) return;

    const poll = async () => {
      const user = auth.currentUser;
      if (!user) return;

      await user.reload();
      if (user.emailVerified) {
        if (verificationPollRef.current) clearInterval(verificationPollRef.current);
        await completeOpenRegistration();
      }
    };

    verificationPollRef.current = setInterval(poll, 3000);
    return () => {
      if (verificationPollRef.current) clearInterval(verificationPollRef.current);
    };
  }, [awaitingVerification]);

  const completeOpenRegistration = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Get a fresh token that includes emailVerified=true
      const token = await user.getIdToken(true);

      const registerResponse = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!registerResponse.ok) {
        const errorText = await registerResponse.text();
        console.error('Failed to register:', errorText);
        setVerificationError('Failed to complete registration. Please try again.');
        return;
      }

      setAgreementsVerified();
      window.location.href = '/';
    } catch (err) {
      console.error('Error completing registration:', err);
      setVerificationError('An error occurred. Please try again.');
    }
  };

  const resendVerificationEmail = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${SHOP_API_BASE_URL}/api/v1/verify-email/send`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          setVerificationError('');
        } else {
          setVerificationError('Failed to resend verification email. Please wait a moment and try again.');
        }
      } catch (err) {
        setVerificationError('Failed to resend verification email. Please wait a moment and try again.');
      }
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      setRegisterError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setRegisterError('Passwords do not match.');
      return;
    }

    if (!agreedToTerms) {
      setRegisterError('You must agree to the Terms of Service.');
      return;
    }

    setRegistering(true);
    setRegisterError('');

    try {
      // Step 1: Create Firebase account
      await signup(email, password, displayName || undefined);

      const user = auth.currentUser;
      if (!user) {
        setRegisterError('Account created but authentication failed. Please log in and try again.');
        return;
      }

      const token = await user.getIdToken();

      // Step 2: Record user agreements (blocking)
      // Record exactly the version shop-svc says is current (and its versioned page), never a
      // hard-coded constant. getTosConfig never rejects; it falls back to the built-in version.
      const tos = await getTosConfig();
      const agreementsResponse = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/user-agreements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agreements: [
              {
                agreementType: 'TERMS_OF_SERVICE',
                agreementVersion: tos.currentVersion,
                documentUrl: tos.documentUrl,
              },
            ],
            acceptedVia: requireInviteCode ? 'invite_code' : 'open_registration',
          }),
        }
      );

      if (!agreementsResponse.ok) {
        const errorText = await agreementsResponse.text();
        console.error('Failed to record agreements:', errorText);
        setRegisterError('Failed to record agreement acceptance. Please try again.');
        return;
      }

      // Step 3: Create shop + activate trial
      if (requireInviteCode) {
        const trimmed = inviteCode.trim().toUpperCase();

        const redeemResponse = await fetch(
          `${SHOP_API_BASE_URL}/api/v1/invite-codes/${encodeURIComponent(trimmed)}/redeem`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!redeemResponse.ok) {
          const errorText = await redeemResponse.text();
          console.error('Failed to redeem invite code:', errorText);
        }

        // Mark agreements as verified so ProtectedRoute skips the re-check on reload
        setAgreementsVerified();

        // Full page reload to ensure all contexts (ShopContext, etc.) initialize fresh
        window.location.href = '/';
      } else {
        // Open registration: send verification email via backend, then wait for verification
        const verifyResponse = await fetch(
          `${SHOP_API_BASE_URL}/api/v1/verify-email/send`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!verifyResponse.ok) {
          const errorText = await verifyResponse.text();
          console.error('Failed to send verification email:', errorText);
          setRegisterError('Failed to send verification email. Please try again.');
          return;
        }

        setAwaitingVerification(true);
      }
    } catch (err: any) {
      console.error('Registration error:', err);

      if (err.code === 'auth/email-already-in-use') {
        setRegisterError('An account with this email already exists. Please log in instead.');
      } else if (err.code === 'auth/weak-password') {
        setRegisterError('Password is too weak. Please use at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setRegisterError('Please enter a valid email address.');
      } else {
        setRegisterError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setRegistering(false);
    }
  };

  // Already authenticated — redirect to dashboard (but not while registration or verification is in progress)
  if (currentUser && !registering && !awaitingVerification) {
    return <Navigate to="/" replace />;
  }

  // Email verification interstitial (open registration only)
  if (awaitingVerification) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>Verify Your Email</h2>
            <p className="login-subtitle">
              We sent a verification link to <strong>{email}</strong>. Please check your inbox and click the link to continue.
            </p>

            {verificationError && <div className="error-message">{verificationError}</div>}

            <div style={{ textAlign: 'center', margin: '24px 0' }}>
              <div className="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full" style={{ marginBottom: '8px', color: 'var(--text-accent)' }}></div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Waiting for verification...</p>
            </div>

            <button
              type="button"
              className="login-btn"
              style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-accent)', border: '1px solid var(--border-medium)', width: '100%' }}
              onClick={resendVerificationEmail}
            >
              Resend Verification Email
            </button>

            <div className="login-footer">
              <p>Didn't receive the email? Check your spam folder.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Invite code entry
  if (!codeValid) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>Sign Up</h2>
            <p className="login-subtitle">Enter your invite code to create an account.</p>

            {codeError && <div className="error-message">{codeError}</div>}

            <form onSubmit={handleValidateCode}>
              <div className="form-group">
                <label>Invite Code</label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="CSAI-XXXX-XXXX"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="login-btn"
                disabled={validating}
              >
                {validating ? 'Validating...' : 'Validate Code'}
              </button>
            </form>

            <div className="login-footer">
              <p>Already have an account? <Link to="/login">Log in</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Registration form
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form">
          <h2>Create Your Account</h2>
          <p className="login-subtitle">{requireInviteCode ? 'Invite code accepted. Fill in your details to get started.' : 'Fill in your details to get started.'}</p>

          {registerError && <div className="error-message">{registerError}</div>}

          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label>Display Name (optional)</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  style={{ paddingRight: '50px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    padding: '4px',
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  style={{ paddingRight: '50px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    padding: '4px',
                  }}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  style={{ marginTop: '3px', flexShrink: 0 }}
                />
                <span>
                  I agree to the{' '}
                  <a href={tosConfig.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>
                    Terms of Service
                  </a>
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              {requireInviteCode && (
                <button
                  type="button"
                  style={{
                    flex: 0,
                    whiteSpace: 'nowrap',
                    padding: '10px 24px',
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '6px',
                    color: 'var(--text-accent)',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setCodeValid(false);
                    setRegisterError('');
                  }}
                >
                  Back
                </button>
              )}

              <button
                type="submit"
                className="login-btn"
                style={{ flex: 1 }}
                disabled={registering || !agreedToTerms}
              >
                {registering ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <div className="login-footer">
            <p>Already have an account? <Link to="/login">Log in</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
